import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Volume2, Square, Pause, Play } from "lucide-react";

interface ArticleListenButtonProps {
  content: string | null;
  title: string;
}

const stripHtmlAndMarkdown = (text: string): string => {
  return text
    .replace(/<[^>]*>/g, " ")
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/>\s/g, "")
    .replace(/[-*+]\s/g, "")
    .replace(/\d+\.\s/g, "")
    .replace(/---/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const ArticleListenButton = ({ content, title }: ArticleListenButtonProps) => {
  const [status, setStatus] = useState<"idle" | "playing" | "paused">("idle");
  const [supported, setSupported] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    setSupported("speechSynthesis" in window);
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  const handlePlay = useCallback(() => {
    if (!content || !supported) return;

    if (status === "paused") {
      window.speechSynthesis.resume();
      setStatus("playing");
      return;
    }

    window.speechSynthesis.cancel();

    const plainText = stripHtmlAndMarkdown(content);
    const utterance = new SpeechSynthesisUtterance(plainText);
    utterance.lang = "en-GB";
    utterance.rate = 1;

    // Try to pick a good English voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(
      (v) => v.lang.startsWith("en-GB") && v.name.includes("Google")
    ) || voices.find((v) => v.lang.startsWith("en-GB"))
      || voices.find((v) => v.lang.startsWith("en"));
    if (preferred) utterance.voice = preferred;

    utterance.onend = () => setStatus("idle");
    utterance.onerror = () => setStatus("idle");

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setStatus("playing");
  }, [content, status, supported]);

  const handlePause = useCallback(() => {
    window.speechSynthesis.pause();
    setStatus("paused");
  }, []);

  const handleStop = useCallback(() => {
    window.speechSynthesis.cancel();
    setStatus("idle");
  }, []);

  if (!supported) return null;

  return (
    <div className="flex items-center gap-2">
      {status === "idle" && (
        <Button variant="outline" size="sm" onClick={handlePlay} className="gap-2 bg-primary text-primary-foreground border-primary hover:bg-primary/90">
          <Volume2 className="h-4 w-4" />
          Listen to article
        </Button>
      )}
      {status === "playing" && (
        <>
          <Button variant="outline" size="sm" onClick={handlePause} className="gap-2">
            <Pause className="h-4 w-4" />
            Pause
          </Button>
          <Button variant="ghost" size="sm" onClick={handleStop}>
            <Square className="h-4 w-4" />
          </Button>
        </>
      )}
      {status === "paused" && (
        <>
          <Button variant="outline" size="sm" onClick={handlePlay} className="gap-2">
            <Play className="h-4 w-4" />
            Resume
          </Button>
          <Button variant="ghost" size="sm" onClick={handleStop}>
            <Square className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
};

export default ArticleListenButton;
